<template>
  <div class="chat-widget__instruction">
    <div class="chat-widget__instruction-wrapper">
      <div v-if="appState.mode === MODE_UML">
        <p>
          Инструмент предназначен для автоматического создания наглядных и информативных схем на основе вашего
          программного кода или описания задачи.
        </p>
        <ul>
          <li>Вставьте ваш код или описание задачи.</li>
          <li>Выберите инструмент (Mind Map, круговая диаграмма, Диаграмма классов, ER, Ганта, состояний и т.д) рядом с
            кнопкой "Отправить".
          </li>
          <li v-if="appState.attachFiles">
            Если нужно прикрепить файл - нажмите на иконку "Скрепка" или перенесите файл в поле ввода (png, jpeg)
          </li>
          <li>Нажмите кнопку "Отправить", и наш алгоритм проанализирует ваш код, определит его структуру и логику, а
            затем создаст соответствующую схему (диаграмму).
          </li>
          <li>Сохраните в форматах PNG или SVG, для дальнейшего использования.</li>
          <li>Для редактирования, скачайте SVG вариант и вставьте в любой редактор svg (например, <a
              href="https://mediamodifier.com/svg-editor">https://mediamodifier.com/svg-editor</a>, загрузите
            изображение, выделите и разгруппируйте)
          </li>
        </ul>
      </div>
      <div v-else-if="appState.mode === MODE_MUSIC">
        <p>
          Инструмент предназначен для автоматической генерации музыки при помощи ИИ на основе описания или текста песни.
        </p>
        <ul>
          <li>Вставьте ваш текст песни или описание.</li>
          <li>Выберите модель.</li>
          <li>Нажмите кнопку "Отправить".</li>
        </ul>
        <p>
          Пример генерации:
        </p>

        <div class="chat-widget__music-example">
          <div class="chat-widget__music-player-wrapper">
            <Suspense>
              <MusicAttachments :files="audioList"/>
            </Suspense>
          </div>
          <div class="chat-widget__music-example-wrapper">
            <p>
              При каждой генерации Suno создает два варианта трека по одному запросу, предоставляя пользователю выбор.
            </p>
            <p>
              Среднее время генерации - 2 минуты.
            </p>
            <p>
              Пример стиля: Винтажный джаз-лаундж, классические стандарты, плавные соло на трубе, контрабас и знойный
              женский вокал.
            </p>
            <p>
              Методичка Suno на русском: <a target="_blank" href="/assets/Suno_Udio_StableAudio. Владислав Эль.pdf">Suno_Udio_StableAudio.
              Владислав Эль</a>
            </p>
          </div>
        </div>
      </div>
      <ul v-else>
        <li>
          Напишите что-нибудь в поле ввода сообщения
        </li>
        <li v-if="appState.attachFiles">
          Если нужно прикрепить файл - нажмите на иконку "Скрепка" (<i class="pi pi-paperclip"></i>) или перенесите файл
          в поле ввода
        </li>
        <li v-if="appState.features.imageVariation">
          Прикрепить можно 1 файл, формата jpeg и png. Прикрепите картинку, чтобы использовать ее как источник для
          генерации.
        </li>
        <li v-if="appState.features.attachVectorDatabase">
          Вы можете добавить к диалогу свою собственную базу знаний (embedding) - нажмите на иконку "База знаний" (<i
            class="pi pi-database"></i>)
        </li>
        <li v-if="appState.features.imageVariation">
          Вы можете создать другой вариант изображения на основе исходного, нажмите на иконку <i
            class="pi pi-palette"></i>
        </li>
        <li>
          Нажмите кнопку "Отправить" или "Ctrl + Enter"
        </li>
      </ul>
      <div v-if="appState.mode === MODE_ONLINE">
        <p>
          Модель перед запросом запрашивают свежие данные в Интернете, после чего
          формируют ответ – и, например, могут отвечать о погоде сегодня, о курсе доллара и пр. Также модели можно
          передавать ссылку на конкретную страницу в Интернете и с высокой вероятностью получить релевантный ответ.
        </p>
        <p>
          Ссылается и выводит список использованных источников. Можно искать по конкретному сайту.
        </p>
        <p>
          Отвечает только на последний вопрос (не держит контекст, связано с ограничениями нейросети).
        </p>
      </div>
      <div v-else-if="appState.mode === MODE_UML || appState.mode === MODE_IMAGE">
        <br>
        <p>
          Инструмент также доступен на <a href="/lk/board">интерактивной доске</a> (более удобный интерфейс).
        </p>
      </div>
      <CarouselComponent :images="images" v-if="appState.showExamples && images && images.length"/>
    </div>
  </div>
</template>

<script setup>

import {computed, defineAsyncComponent} from "vue";
import CarouselComponent from "@/modules/chat/components/instruction/CarouselComponent.vue";
import {MODE_IMAGE, MODE_MUSIC, MODE_ONLINE, MODE_UML} from "@/constants";
import {useAppStateStore} from "@/modules/chat/stores/appStore";
import {useReferenceStore} from "@/modules/chat/stores/referenceStore";

const MusicAttachments = defineAsyncComponent(() =>
    import("@/modules/chat/components/message/MusicAttachments.vue")
);

const audioList = [
  {
    url: '/assets/music/example.mp3',
    title: 'Свобода',
    cover: '/assets/music/example.jpg',
  },
];

const appState = useAppStateStore();

const images = computed(() => {
  if (appState.mode === MODE_IMAGE) {
    return useReferenceStore().examples.images;
  }
  if (appState.mode === MODE_UML) {
    return useReferenceStore().examples.uml;
  }
  return [];
});
</script>